"use client";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useEffect } from "react";
import { useMediaQuery } from "react-responsive";

import { useClassroomStore } from "../store/useClassroomStore";

import MobileControlsDialog from "./MobileControlsDialog";

import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar.tsx";

interface ClassroomTabsProps {
  isScreenSharing: boolean;
}

export default function ClassroomTabs({ isScreenSharing }: ClassroomTabsProps) {
  const { t } = useTranslation(["common", "classroom"]);
  const {
    view,
    setView,
    presenterId,
    showParticipantsOnMobile,
    setShowParticipantsOnMobile,
    isChatOpen,
    toggleChat,
    unreadMessageCount,
    videoSync,
  } = useClassroomStore();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ minWidth: 769, maxWidth: 1280 });

  // Only handle the case when screen sharing ends and we're on the screenshare tab
  useEffect(() => {
    if (!isScreenSharing && view === "screenshare") {
      // If screen share ends while on screenshare tab, switch to call
      setView("call");
    }
  }, [isScreenSharing, view, setView]);

  // Handle tab click
  const handleTabClick = (tabView: "call" | "whiteboard" | "screenshare" | "video") => {
    setView(tabView);
  };

  // Check if we're in a fullscreen activity for participant toggle
  const isInFullscreenActivity =
    view === "whiteboard" || (view === "screenshare" && presenterId) || view === "video";

  // Mobile Layout (≤768px)
  if (isMobile) {
    return (
      <div className="flex w-full items-center justify-between gap-2 p-1">
        {/* Left side: Mobile controls */}
        <div className="flex flex-shrink-0 items-center gap-1">
          {/* Sidebar Trigger */}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <SidebarTrigger className="bg-background/80 hover:bg-background/90 border-border h-9 w-9 rounded-lg shadow-sm backdrop-blur-sm" />
          </motion.div>

          {/* Participant Toggle - only show in fullscreen activities */}
          {isInFullscreenActivity && (
            <motion.button
              animate={{ scale: 1, opacity: 1 }}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-lg shadow-sm backdrop-blur-sm transition-all",
                showParticipantsOnMobile
                  ? "bg-primary hover:bg-primary/90 border-primary text-primary-foreground"
                  : "bg-background/80 hover:bg-background/90 border-border text-accent-foreground",
              )}
              initial={{ scale: 0, opacity: 0 }}
              title={
                showParticipantsOnMobile
                  ? t("classroom:hideParticipants" as any)
                  : t("classroom:showParticipants" as any)
              }
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowParticipantsOnMobile(!showParticipantsOnMobile)}
            >
              <Icon
                height="14"
                icon={showParticipantsOnMobile ? "lucide:users" : "lucide:users-x"}
                width="14"
              />
            </motion.button>
          )}

          {/* Chat Toggle */}
          <motion.div className="relative">
            <motion.button
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-lg shadow-sm backdrop-blur-sm transition-all",
                isChatOpen
                  ? "bg-primary hover:bg-primary/90 border-primary text-primary-foreground"
                  : "bg-background/80 hover:bg-background/90 border-border text-accent-foreground",
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleChat}
            >
              <Icon
                height="14"
                icon={isChatOpen ? "lucide:message-square-text" : "lucide:message-square"}
                width="14"
              />
            </motion.button>

            {/* Unread message count badge */}
            {unreadMessageCount > 0 && !isChatOpen && (
              <motion.div
                animate={{ scale: 1, opacity: 1 }}
                className="bg-destructive text-destructive-foreground absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold shadow-sm"
                initial={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Center: View tabs - optimized for mobile */}
        <div className="bg-background/95 border-border flex flex-1 items-center justify-center rounded-t-xl shadow-sm backdrop-blur-sm">
          <Tab
            active={view === "call"}
            icon="lucide:video"
            isMobile={true}
            label=""
            onClick={() => handleTabClick("call")}
          />
          <Tab
            active={view === "whiteboard"}
            icon="lucide:pen-tool"
            isMobile={true}
            label=""
            onClick={() => handleTabClick("whiteboard")}
          />
          {isScreenSharing && (
            <Tab
              active={view === "screenshare"}
              icon="lucide:monitor"
              isMobile={true}
              label=""
              onClick={() => handleTabClick("screenshare")}
            />
          )}
          {videoSync && (
            <Tab
              active={view === "video"}
              icon="lucide:play-circle"
              isMobile={true}
              label=""
              onClick={() => handleTabClick("video")}
            />
          )}
        </div>

        {/* Right side: Settings */}
        <div className="flex-shrink-0">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <MobileControlsDialog />
          </motion.div>
        </div>
      </div>
    );
  }

  // Tablet Layout (769px-1280px) - Like desktop but with sidebar trigger
  if (isTablet) {
    return (
      <div className="flex w-full items-center gap-4">
        {/* Left side: Sidebar trigger for tablets */}
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <SidebarTrigger className="bg-background/80 hover:bg-background/90 border-border h-10 w-10 rounded-lg shadow-sm backdrop-blur-sm" />
        </motion.div>

        {/* Center: View tabs - full desktop style */}
        <div className="bg-background/95 border-border flex flex-shrink-0 items-center rounded-t-xl shadow-sm backdrop-blur-sm">
          <Tab
            active={view === "call"}
            icon="lucide:video"
            isMobile={false}
            label={t("classroom:callView")}
            onClick={() => handleTabClick("call")}
          />
          <Tab
            active={view === "whiteboard"}
            icon="lucide:pen-tool"
            isMobile={false}
            label={t("classroom:whiteboard")}
            onClick={() => handleTabClick("whiteboard")}
          />
          {isScreenSharing && (
            <Tab
              active={view === "screenshare"}
              icon="lucide:monitor"
              isMobile={false}
              label={t("classroom:screenShare")}
              onClick={() => handleTabClick("screenshare")}
            />
          )}
          {videoSync && (
            <Tab
              active={view === "video"}
              icon="lucide:play-circle"
              isMobile={false}
              label={t("classroom:videoRoom")}
              onClick={() => handleTabClick("video")}
            />
          )}
        </div>
      </div>
    );
  }

  // Desktop Layout (>1280px) - Clean tabs only
  return (
    <div className="flex w-full items-center justify-center">
      {/* Center: View tabs - clean desktop style */}
      <div className="bg-background/95 border-border flex flex-shrink-0 items-center rounded-t-xl shadow-sm backdrop-blur-sm">
        <Tab
          active={view === "call"}
          icon="lucide:video"
          isMobile={false}
          label={t("classroom:callView")}
          onClick={() => handleTabClick("call")}
        />
        <Tab
          active={view === "whiteboard"}
          icon="lucide:pen-tool"
          isMobile={false}
          label={t("classroom:whiteboard")}
          onClick={() => handleTabClick("whiteboard")}
        />
        {isScreenSharing && (
          <Tab
            active={view === "screenshare"}
            icon="lucide:monitor"
            isMobile={false}
            label={t("classroom:screenShare")}
            onClick={() => handleTabClick("screenshare")}
          />
        )}
        {videoSync && (
          <Tab
            active={view === "video"}
            icon="lucide:play-circle"
            isMobile={false}
            label={t("classroom:videoRoom")}
            onClick={() => handleTabClick("video")}
          />
        )}
      </div>
    </div>
  );
}

interface TabProps {
  icon: string;
  label: string;
  active: boolean;
  isMobile: boolean;
  onClick: () => void;
}

function Tab({ icon, label, active, isMobile, onClick }: TabProps) {
  return (
    <button
      className={cn(
        "relative flex items-center gap-2 backdrop-blur-xl transition-all",
        "text-muted-foreground hover:text-accent-foreground bg-muted/30 hover:bg-accent-foreground/5",
        active && "text-accent-foreground bg-accent-foreground/10",
        // Responsive sizing with consistent design
        isMobile ? "h-10 min-w-0 flex-1 justify-center px-2" : "h-12 min-w-[120px] px-6",
        // Ensure first tab has rounded top-left, last tab has rounded top-right
        "first:rounded-tl-xl last:rounded-tr-xl",
      )}
      onClick={onClick}
    >
      <Icon height="16" icon={icon} width="16" />
      {label && <span className="truncate font-medium">{label}</span>}

      {active && (
        <motion.div
          className="bg-accent-foreground absolute bottom-0 left-0 h-0.5 w-full"
          layoutId="tab-indicator"
        />
      )}
    </button>
  );
}
